import i18n from '@/i18n'
import { sharedHelper } from '@/utils'

/**
 * Shared validation UX for the CRM forms.
 *
 * One rule everywhere: an invalid field gets a red border (`is-invalid`) and the
 * reason printed under it. Client-side (vuelidate) messages and the server's
 * field errors go through the same two helpers, so a Laravel rule that the form
 * doesn't know about still lands on the right input instead of only in a toast.
 *
 * Server errors are keyed by the API field name, so a form whose model key
 * differs from the payload key maps it in `serverFieldMap`.
 */
export default {
  data() {
    return {
      serverErrors: {},
      submitting: false,
    }
  },
  computed: {
    /** Override in a component when payload keys differ from form keys. */
    serverFieldMap() {
      return {}
    },
  },
  methods: {
    /** Reset before every submit so stale errors never linger. */
    clearServerErrors() {
      this.serverErrors = {}
    },
    /** A single field's error: server first (it knows more), then vuelidate. */
    fieldError(name, label) {
      const serverKey = this.serverFieldMap[name] || name
      if (this.serverErrors[serverKey]) return this.serverErrors[serverKey]

      const v = this.$v && this.$v.form ? this.$v.form[name] : null
      if (!v || !v.$dirty || !v.$invalid) return null

      const text = label || name
      // Phone/email are an either-or pair, so the message names both.
      if (v.requiredEither === false) return i18n.t('message.requiredPhoneOrEmail')
      if (v.required === false) return i18n.t('message.requiredX', [text])
      if (v.email === false) return i18n.t('message.validX', [text])
      if (v.minValue === false || v.maxValue === false || v.numeric === false) {
        return i18n.t('message.invalidX', [text])
      }
      return i18n.t('message.invalidX', [text])
    },
    /** `true` when the input should be painted red. */
    isInvalid(name) {
      return !!this.fieldError(name)
    },
    /** bootstrap-vue `:state` wants null (untouched) / false (bad) / true (ok). */
    fieldState(name) {
      if (this.fieldError(name)) return false
      const v = this.$v && this.$v.form ? this.$v.form[name] : null
      if (!v || !v.$dirty) return null
      return true
    },
    /** Typing in a field clears its server error so the red box goes away. */
    onFieldInput(name) {
      const serverKey = this.serverFieldMap[name] || name
      if (this.serverErrors[serverKey]) {
        this.$delete(this.serverErrors, serverKey)
      }
    },
    /**
     * Runs local validation, then the given request. Returns the API result on
     * success (after a success alert) or null, having painted the failing
     * fields and shown the error alert.
     */
    async submitForm(request, { successMessage, errorContext } = {}) {
      this.clearServerErrors()
      if (this.$v && this.$v.form) {
        this.$v.form.$touch()
        if (this.$v.form.$invalid) {
          this.$nextTick(() => this.scrollToFirstError())
          return null
        }
      }

      this.submitting = true
      try {
        const result = await request()
        if (result) {
          if (successMessage) sharedHelper.alertSuccessMessage(successMessage)
          return result
        }

        // The store already alerted; surface the same errors inline.
        const lastError = errorContext ? errorContext() : null
        if (lastError) {
          this.serverErrors = sharedHelper.getFieldErrors(lastError)
          this.$nextTick(() => this.scrollToFirstError())
        }
        return null
      } finally {
        this.submitting = false
      }
    },
    scrollToFirstError() {
      const el = this.$el.querySelector('.is-invalid, .invalid-feedback')
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    },
  },
}
